import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Api from "./utils/apiCall";
import { API_URLS } from "./apis/urls";

interface EmailTemplateData {
  id: string;
  title: string;
  subject: string;
  fromEmailId: string;
  ccEmailId?: string;
  bccEmailId?: string;
  msg: string;
  status: boolean;
  created_at?: string;
  updated_at?: string;
}

interface EmailTemplatesResponse {
  statusCode: number;
  message: string;
  data: {
    data: EmailTemplateData[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface EmailTemplatesState {
  templatesData: EmailTemplatesResponse | null;
  templateDetails: any;
  createData: any;
  deleteData: any;
  loading: "idle" | "pending" | "fulfilled" | "rejected";
  error: string | null;
  action: "list" | "create" | "get_by_id" | "delete" | null;
}

const emailTemplatesInitialState: EmailTemplatesState = {
  templatesData: null,
  templateDetails: null,
  createData: null,
  deleteData: null,
  loading: "idle",
  error: null,
  action: null,
};

export const getEmailTemplates: any = createAsyncThunk(
  "emailTemplates/getEmailTemplates",
  async (payload: { page?: number; limit?: number; query?: string }, thunkAPI) => {
    try {
      const requestBody = {
        page: payload.page || 1,
        limit: payload.limit || 10,
        query: payload.query || "",
      };

      const response = await Api.post(
        API_URLS.EMAIL_TEMPLATES.LIST,
        requestBody
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const createEmailTemplate: any = createAsyncThunk(
  "emailTemplates/createEmailTemplate",
  async (payload: {
    id?: string;
    title: string;
    subject: string;
    fromEmailId: string;
    ccEmailId?: string;
    bccEmailId?: string;
    msg: string;
    status: boolean;
  }, thunkAPI) => {
    try {
      const requestBody = {
        ...(payload.id && { id: payload.id }),
        title: payload.title,
        subject: payload.subject,
        fromEmailId: payload.fromEmailId,
        ...(payload.ccEmailId && { ccEmailId: payload.ccEmailId }),
        ...(payload.bccEmailId && { bccEmailId: payload.bccEmailId }),
        msg: payload.msg,
        status: payload.status,
      };

      const response = await Api.post(
        API_URLS.EMAIL_TEMPLATES.CREATE,
        requestBody
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getEmailTemplateById: any = createAsyncThunk(
  "emailTemplates/getEmailTemplateById",
  async (payload: { id: string }, thunkAPI) => {
    try {
      const requestBody = {
        id: payload.id,
      };

      const response = await Api.post(
        API_URLS.EMAIL_TEMPLATES.GET_BY_ID,
        requestBody
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const deleteEmailTemplate: any = createAsyncThunk(
  "emailTemplates/deleteEmailTemplate",
  async (payload: { id: string }, thunkAPI) => {
    try {
      const requestBody = {
        id: payload.id,
      };

      const response = await Api.post(
        API_URLS.EMAIL_TEMPLATES.DELETE,
        requestBody
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const resetAll = () => ({
  type: "emailTemplates/resetAll",
});

const emailTemplatesSlice = createSlice({
  name: "emailTemplatesState",
  initialState: emailTemplatesInitialState,
  reducers: {
    resetAll: (state) => {
      state.templatesData = null;
      state.templateDetails = null;
      state.createData = null;
      state.deleteData = null;
      state.loading = "idle";
      state.error = null;
      state.action = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Email Templates
      .addCase(getEmailTemplates.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "list";
      })
      .addCase(getEmailTemplates.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.templatesData = action.payload;
        state.error = null;
      })
      .addCase(getEmailTemplates.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload;
      })

      // Create Email Template
      .addCase(createEmailTemplate.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "create";
      })
      .addCase(createEmailTemplate.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.createData = action.payload;
        state.error = null;
      })
      .addCase(createEmailTemplate.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload;
      })

      // Get Email Template by ID
      .addCase(getEmailTemplateById.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "get_by_id";
      })
      .addCase(getEmailTemplateById.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.templateDetails = action.payload;
        state.error = null;
      })
      .addCase(getEmailTemplateById.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload;
      })

      // Delete Email Template
      .addCase(deleteEmailTemplate.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "delete";
      })
      .addCase(deleteEmailTemplate.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.deleteData = action.payload;
        state.error = null;
      })
      .addCase(deleteEmailTemplate.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload;
      });
  },
});

export const { resetAll: resetEmailTemplatesAll } = emailTemplatesSlice.actions;
export default emailTemplatesSlice.reducer;
